import React, { Fragment } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import MuiTable, { TableProps as MuiTableProps } from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableRow, { TableRowProps } from '@mui/material/TableRow'
import TableBody from '@mui/material/TableBody'
import TableCell, { TableCellProps } from '@mui/material/TableCell'
import TableFooter from '@mui/material/TableFooter'

import Pendable from '../Pendable'
import { Pagination, PaginationProps } from './Pagination'
import ColumnHeader, { ColumnHeaderProps } from './ColumnHeader'
import Divider from '@mui/material/Divider'
import type { CSSObject, Theme } from '@mui/material/styles'
import { useStickyHorizontalScroll, visibleScrollbarSx } from '../StickyHorizontalScroll'
import { ActionMenuRow } from './ActionMenuContext'

const styles = {
    wrapper: {
        position: 'relative',
    },
    cell: {
        borderBottom: 'none',
    },
}

/**
 * Flex column that may shrink below its content. Every element between a `fillHeight` page and a
 * `stickyHeader` table needs it, so the height limit reaches the table's row scroller.
 */
export const shrinkableColumnSx: CSSObject = {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    // Doubled class outranks the fillHeight page's rule that stops its other children shrinking
    '&&': { flexShrink: 1 },
}

/** Row scroller of a `stickyHeader` table, for pages that build their own MUI table */
export const rowScrollerSx = (theme: Theme): CSSObject => ({
    overflow: 'auto',
    minHeight: 0,
    ...visibleScrollbarSx(theme),
    // MUI paints sticky header cells with the page background; the table sits on a paper
    '& .MuiTableCell-stickyHeader': { backgroundColor: theme.palette.background.paper },
})

interface CellProp extends TableCellProps {
    value: string | React.ReactNode
    noTypography?: true
}
const Cell = ({ value, noTypography, ...rest }: CellProp) => (
    <TableCell {...rest}>{noTypography ? value : <Typography component="div">{value}</Typography>}</TableCell>
)

function filterFn<TEntity, TSort extends string = string>(
    column: ColumnProps<TEntity, TSort> | null
): column is ColumnProps<TEntity, TSort> {
    return column !== null
}

const omitNoTypography = (
    obj: (TableCellProps & Pick<CellProp, 'noTypography'>) | undefined
): TableCellProps | undefined => {
    if (!obj) return undefined
    const { noTypography: _, ...rest } = obj
    return rest
}

export interface ColumnProps<TEntity, TSort extends string = string> {
    title?: React.ReactNode
    getValue: (ent: TEntity, index: number) => React.ReactNode
    props?: TableCellProps & Pick<CellProp, 'noTypography'>
    headerProps?: TableCellProps
    sorting?: ColumnHeaderProps<TSort>
}

export interface TableConfig<TEntity, TSort extends string = string> extends Array<ColumnProps<
    TEntity,
    TSort
> | null> {}

export interface TableProps<TEntity, TSort extends string = string> {
    config: TableConfig<TEntity, TSort>
    sorting?: {
        currentSortKey?: { asc?: TSort } | { desc?: TSort }
        onSortOrderChanged?: (sortOrder: { asc?: TSort } | { desc?: TSort }) => void
    }
    data: Array<TEntity>
    getCustomEntityId?: (row: TEntity) => string
    emptyMessageComponent?: React.ReactNode
    hasBorders?: boolean
    id?: string
    isSmall?: boolean
    pagination?: PaginationProps
    noHeader?: boolean
    pending?: boolean
    footer?: () => React.ReactElement
    /**
     * Scrolls the rows inside the table with the header row pinned, instead of scrolling the page.
     * The table fills the height its parents allow: use a `fillHeight` page and `shrinkableColumnSx`
     * on each element in between.
     */
    stickyHeader?: boolean

    renderExpandedRow?: (entity: TEntity, index: number) => React.ReactNode | null
    props?: MuiTableProps
    rowProps?: (ent: TEntity) => TableRowProps
}

/**
 * Common table component
 * @param config - array of column descriptions {
 *   title - column title,
 *   getValue - function to get the value to show for each row,
 *   props - MaterialUI cell properties for rows cells (and noTypography - if we don't want the value to be wrapped with Typography)
 *   headerProps - MaterialUI cell properties for header row cell
 *   sorting - sorting properties
 * }
 * @param data - array of entities
 * @param getCustomEntityId - Pass this when you wish to pass a custom entity id/key fn, e.g. if the entity id consists of multiple properties. Defaults to the 'id' of the entity (if any), else the entity's index.
 * @param props - MaterialUI Table properties
 * @param id
 * @param hasBorders - whether we should show lines borders
 * @param emptyMessageComponent - what should we show if no entities present
 * @param isSmall - mostly there are two default table sizes for paddings
 * @param rowProps - function to get MaterialUI table row properties based on row data
 * @param pagination - should we show pagination and how we should do it
 * @param noHeader - should we hide header row
 * @param pending - whether we should show loading spinner
 * @param stickyHeader - scroll the rows inside the table with the header pinned
 */
export const Table: <T, TSort extends string = string>(
    props: TableProps<T, TSort>
) => React.ReactElement<TableProps<T, TSort>> = ({
    config: initConfig,
    data,
    getCustomEntityId,
    props,
    id,
    hasBorders,
    emptyMessageComponent = <div />,
    isSmall,
    renderExpandedRow,
    rowProps,
    pagination,
    noHeader,
    pending,
    footer,
    sorting,
    stickyHeader,
}) => {
    const config = initConfig.filter(filterFn)
    const hscroll = useStickyHorizontalScroll(data)
    const getEntityId = (entity: (typeof data)[0], index: number): string =>
        getCustomEntityId?.(entity) || ((entity as Record<string, unknown>)?.id as string) || String(index)
    return (
        <Pendable pending={!!pending} cover={!!data.length} id={id} sx={stickyHeader ? shrinkableColumnSx : undefined}>
            {data.length ? (
                // A row scroller keeps its own scrollbars in view, so it needs no horizontal proxy
                <Box
                    ref={stickyHeader ? undefined : hscroll.viewportRef}
                    onScroll={stickyHeader ? undefined : hscroll.onViewportScroll}
                    sx={[styles.wrapper, stickyHeader ? rowScrollerSx : hscroll.viewportSx]}
                >
                    <MuiTable id={id} size={isSmall ? 'small' : 'medium'} stickyHeader={stickyHeader} {...props}>
                        {!noHeader && (
                            <TableHead>
                                <TableRow>
                                    {config.map(
                                        (
                                            { title = '', props, headerProps, sorting: columnSorting },
                                            ind,
                                            { length }
                                        ) => (
                                            <TableCell
                                                key={ind}
                                                sx={{
                                                    ...(isSmall && !hasBorders ? styles.cell : {}),
                                                    ...(isSmall && (!ind || ind === length - 1)
                                                        ? { px: 1, py: 0 }
                                                        : {}),
                                                }}
                                                data-table-column={title}
                                                {...(headerProps ? headerProps : omitNoTypography(props))}
                                            >
                                                <ColumnHeader
                                                    title={title}
                                                    sorting={columnSorting}
                                                    currentSortKey={sorting?.currentSortKey}
                                                    onSortOrderChanged={sorting?.onSortOrderChanged}
                                                />
                                            </TableCell>
                                        )
                                    )}
                                </TableRow>
                            </TableHead>
                        )}
                        <TableBody>
                            {data.map((entity, i) => {
                                const expandedContent = renderExpandedRow?.(entity, i) ?? null
                                return (
                                    <Fragment key={getEntityId(entity, i)}>
                                        <ActionMenuRow hover {...(rowProps ? rowProps(entity) : null)}>
                                            {config.map(({ getValue, props, title }, ind, { length }) => (
                                                <Cell
                                                    sx={{
                                                        ...(isSmall && !hasBorders ? styles.cell : {}),
                                                        ...(isSmall && (!ind || ind === length - 1)
                                                            ? { px: 1, py: 0 }
                                                            : {}),
                                                    }}
                                                    key={ind}
                                                    value={getValue(entity, i)}
                                                    data-cell-name={`${title}-cell`}
                                                    {...props}
                                                />
                                            ))}
                                        </ActionMenuRow>
                                        {expandedContent && (
                                            <TableRow>
                                                <TableCell colSpan={config.length} sx={{ p: 0, border: 0 }}>
                                                    {expandedContent}
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </Fragment>
                                )
                            })}
                        </TableBody>
                        {footer && <TableFooter>{footer()}</TableFooter>}
                    </MuiTable>
                </Box>
            ) : (
                <>{emptyMessageComponent}</>
            )}
            {hscroll.scrollbar || pagination ? (
                <Box
                    sx={{
                        position: 'sticky',
                        bottom: 0,
                        zIndex: 2,
                        backgroundColor: 'background.paper',
                    }}
                >
                    {hscroll.scrollbar}
                    {pagination && (
                        <>
                            <Divider />
                            <Pagination {...pagination} id={id} />
                        </>
                    )}
                </Box>
            ) : null}
        </Pendable>
    )
}
